import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { DashboardLayout } from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCcw, Play, Pause, Repeat } from "lucide-react";

const formatCurrency = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v || 0);

export default function RecurringInvoicesPage() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    client_id: "",
    frequency: "monthly",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    invoice_number_prefix: "REC-",
    amount: "",
    tax: "0",
    notes: "",
    payment_terms: "30",
    active: true,
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rec, cli] = await Promise.all([
        axios.get(`${API}/recurring-invoices`, { headers }),
        axios.get(`${API}/clients`, { headers }),
      ]);
      setItems(rec.data);
      setClients(cli.data);
    } catch {
      toast.error("Failed to fetch recurring invoices");
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      client_id: "",
      frequency: "monthly",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      invoice_number_prefix: "REC-",
      amount: "",
      tax: "0",
      notes: "",
      payment_terms: "30",
      active: true,
    });
  };

  const handleEdit = (r) => {
    setEditing(r);
    setFormData({
      client_id: r.client_id,
      frequency: r.frequency,
      start_date: r.start_date,
      end_date: r.end_date || "",
      invoice_number_prefix: r.invoice_number_prefix || "REC-",
      amount: String(r.amount ?? ""),
      tax: String(r.tax ?? "0"),
      notes: r.notes || "",
      payment_terms: String(r.payment_terms ?? "30"),
      active: r.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: formData.client_id,
        frequency: formData.frequency,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        invoice_number_prefix: formData.invoice_number_prefix || null,
        amount: parseFloat(formData.amount) || 0,
        tax: parseFloat(formData.tax) || 0,
        notes: formData.notes || null,
        payment_terms: parseInt(formData.payment_terms) || 30,
        active: formData.active,
      };
      if (editing) {
        await axios.put(`${API}/recurring-invoices/${editing.id}`, payload, { headers });
        toast.success("Recurring invoice updated");
      } else {
        await axios.post(`${API}/recurring-invoices`, payload, { headers });
        toast.success("Recurring invoice created");
      }
      setDialogOpen(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleToggle = async (r) => {
    try {
      await axios.post(`${API}/recurring-invoices/${r.id}/toggle`, {}, { headers });
      toast.success(r.active ? "Paused" : "Resumed");
      fetchAll();
    } catch { toast.error("Failed to toggle"); }
  };

  const handleRunNow = async (r) => {
    if (!window.confirm(`Generate a draft invoice for "${r.client_name}" now?`)) return;
    try {
      await axios.post(`${API}/recurring-invoices/${r.id}/run-now`, {}, { headers });
      toast.success("Draft invoice created — check Invoices page");
      fetchAll();
    } catch { toast.error("Failed to run"); }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete this recurring invoice for "${r.client_name}"?`)) return;
    try {
      await axios.delete(`${API}/recurring-invoices/${r.id}`, { headers });
      toast.success("Deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <DashboardLayout title="Recurring Invoices">
      <div className="space-y-6" data-testid="recurring-invoices-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-sm text-muted-foreground">
              Set up templates that auto-create <strong>draft</strong> invoices on a schedule.
              Drafts appear on the Invoices page for your review and sending.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-hover" data-testid="add-recurring-btn">
                <Plus className="w-4 h-4 mr-2" /> New Recurring Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Recurring Invoice" : "New Recurring Invoice"}</DialogTitle>
                <DialogDescription>Drafts will be created automatically on the schedule.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                  >
                    <SelectTrigger data-testid="recurring-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Frequency *</Label>
                    <Select
                      value={formData.frequency}
                      onValueChange={(v) => setFormData({ ...formData, frequency: v })}
                    >
                      <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment terms (days)</Label>
                    <Input
                      type="number" min="0"
                      value={formData.payment_terms}
                      onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                      data-testid="recurring-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End date (optional)</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (AUD) *</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="recurring-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tax / GST (AUD)</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={formData.tax}
                      onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Invoice # prefix</Label>
                  <Input
                    value={formData.invoice_number_prefix}
                    onChange={(e) => setFormData({ ...formData, invoice_number_prefix: e.target.value })}
                    placeholder="REC-"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Added to each generated invoice"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="rounded border-input"
                  />
                  <Label htmlFor="active" className="cursor-pointer">Active</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-recurring-btn">
                    {editing ? "Update" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader><CardTitle className="section-title">Templates ({items.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center p-6">
                <Repeat className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No recurring invoices</h3>
                <p className="text-muted-foreground mt-1">Create your first template to automate billing.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="table-header">Client</TableHead>
                    <TableHead className="table-header">Frequency</TableHead>
                    <TableHead className="table-header text-right">Amount</TableHead>
                    <TableHead className="table-header">Next Run</TableHead>
                    <TableHead className="table-header">Last Run</TableHead>
                    <TableHead className="table-header text-right">Runs</TableHead>
                    <TableHead className="table-header">Status</TableHead>
                    <TableHead className="table-header text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <TableRow key={r.id} className="hover:bg-accent/50 transition-colors" data-testid={`recurring-row-${r.id}`}>
                      <TableCell className="font-medium">{r.client_name}</TableCell>
                      <TableCell><Badge variant="outline">{r.frequency}</Badge></TableCell>
                      <TableCell className="text-right data-cell">{formatCurrency((r.amount || 0) + (r.tax || 0))}</TableCell>
                      <TableCell className="data-cell">{r.next_run_date}</TableCell>
                      <TableCell className="data-cell">{r.last_run_date || "-"}</TableCell>
                      <TableCell className="text-right">{r.run_count || 0}</TableCell>
                      <TableCell>
                        {r.active
                          ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                          : <Badge variant="outline" className="bg-gray-50 text-gray-500">Paused</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" title="Run now" onClick={() => handleRunNow(r)} data-testid={`recurring-run-${r.id}`}>
                            <RefreshCcw className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title={r.active ? "Pause" : "Resume"} onClick={() => handleToggle(r)} data-testid={`recurring-toggle-${r.id}`}>
                            {r.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(r)} data-testid={`recurring-edit-${r.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(r)}
                            data-testid={`recurring-delete-${r.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
